import { type Edge as BoardEdge } from '@shared/board'
import { exitPoint, type Side } from '@shared/whiteboard/edges'
import { type Rect } from '@shared/whiteboard/geometry'
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  type Edge,
  type EdgeProps,
  type InternalNode,
  Position,
  useInternalNode,
} from '@xyflow/react'
import { clsx } from 'clsx'
import { useState } from 'react'
import { useBoardCtx } from '../../context'
import { EditableTitle } from '@/components/EditableTitle'
import { useI18n } from '@/i18n/i18n'

export type FloatingEdgeType = Edge<{ edge: BoardEdge }, 'floating'>

function rectOf(node: InternalNode): Rect {
  return {
    x: node.internals.positionAbsolute.x,
    y: node.internals.positionAbsolute.y,
    w: node.measured.width ?? 0,
    h: node.measured.height ?? 0,
  }
}

const POSITION: Record<Side, Position> = {
  top: Position.Top,
  right: Position.Right,
  bottom: Position.Bottom,
  left: Position.Left,
}

/** The path between two nodes, leaving and entering on the sides that face each other. */
function usePathBetween(source: string, target: string) {
  const s = useInternalNode(source)
  const t = useInternalNode(target)
  if (!s || !t) return null
  const a = rectOf(s)
  const b = rectOf(t)
  const start = exitPoint(a, b)
  const end = exitPoint(b, a)
  return getBezierPath({
    sourceX: start.x,
    sourceY: start.y,
    sourcePosition: POSITION[start.side],
    targetX: end.x,
    targetY: end.y,
    targetPosition: POSITION[end.side],
  })
}

export type LinkEdgeType = Edge<{ kind: 'anchor' | 'reply' }, 'link'>

/** An annotation line (a message pointing at something, a reply at its parent): drawn, never selected. */
export function LinkEdge({ source, target, data }: EdgeProps<LinkEdgeType>) {
  const p = usePathBetween(source, target)
  if (!p) return null
  return (
    <path
      d={p[0]}
      fill="none"
      className={clsx('moot-link', data?.kind === 'reply' ? 'is-reply' : 'is-anchor')}
    />
  )
}

/** Edges leave and enter on whichever sides face each other. Double-click anywhere on the line to label it. */
export function FloatingEdge({
  id,
  source,
  target,
  markerEnd,
  selected,
  data,
}: EdgeProps<FloatingEdgeType>) {
  const p = usePathBetween(source, target)
  const { client } = useBoardCtx()
  const { t: tr } = useI18n()
  const [editing, setEditing] = useState(false)
  if (!p) return null
  const [path, lx, ly] = p
  const label = data?.edge.label ?? ''
  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        interactionWidth={0}
        className={clsx('moot-edge', selected && 'is-selected')}
        {...(markerEnd !== undefined && { markerEnd })}
      />
      {/* The wide invisible hit area: select by click, label by double-click anywhere on the line. */}
      <path
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth={18}
        className="react-flow__edge-interaction"
        onDoubleClick={(e) => {
          e.stopPropagation()
          if (!client.readOnly) setEditing(true)
        }}
      >
        {!label && <title>{tr('edge.labelHint')}</title>}
      </path>
      {(label || editing) && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan absolute rounded-sm bg-canvas px-1.5 py-px text-[12px] text-muted"
            style={{
              transform: `translate(-50%, -50%) translate(${lx}px, ${ly}px)`,
              pointerEvents: 'all',
            }}
            onDoubleClick={(e) => {
              e.stopPropagation()
              if (!client.readOnly) setEditing(true)
            }}
          >
            <EditableTitle
              value={label}
              editing={editing}
              onEditingChange={setEditing}
              maxLength={200}
              onCommit={(next) => {
                client.apply([{ type: 'edge.update', id, patch: { label: next } }])
              }}
              className={clsx(!label && 'text-muted/60')}
              inputClassName="text-[12px]"
              autosize
            />
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  )
}
